/**
 * GUPM-261 — máquina de estados do botão de plantão da sidebar (TS puro).
 *
 * Entrada: perfil do usuário + plantão ativo (via `get_current_shift`, visível
 * a todos). Saída: o que a sidebar renderiza.
 *
 * O plantão é GLOBAL: quem está de plantão cobre as 3 barragens. Não há estado
 * "plantão desta barragem" — existe UM plantonista por vez em toda a operação.
 *
 * Regras:
 * - "Iniciar plantão" (bootstrap) só existe para OPERADOR e só sem plantão
 *   ativo — o trigger do banco rejeita admin/RT como plantonistas. É caminho
 *   EXCEPCIONAL: em regime normal sempre há alguém de plantão.
 * - Ausência de plantonista é ANOMALIA, não estado neutro: admin/RT recebem
 *   aviso destacado (`sem-plantao-alerta`) em vez de um informativo discreto.
 * - Holder vê "Iniciar troca de plantão" (inclusive num hipotético holder
 *   admin de dado legado: a troca formal ainda é o caminho certo pra ele).
 * - Plantão de OUTRA pessoa: ADMIN e TÉCNICO RESPONSÁVEL ganham a ação de
 *   ENCERRAR (GUPM-313, 27/08/2026 — inverte a decisão de 12/08/2026, que dava
 *   o destrave só ao admin; o operador segue sem a ação).
 * - Falha na consulta NUNCA vira "sem plantão": colapsar os dois faz a UI
 *   oferecer "Iniciar plantão" com plantão alheio ativo — o bug original de
 *   volta, em modo degradado.
 * - Plantão passado de 12 h é anomalia visível (`staleHours`, GUPM-273): quem
 *   passou disso deixou de ser rendido, e isso vale para o holder e para quem
 *   apenas observa — o vencimento é do plantão, não de quem olha.
 * - Plantonista desligado no cadastro (`holderIsActive = false`, GUPM-273) trava
 *   a cadeia: o trigger exige operador ATIVO em quem sai, então ninguém passa e
 *   ninguém assume. Sem dizer isso na tela, a operação fica parada sem
 *   explicação até alguém pensar em chamar um admin.
 */

import { isShiftStale } from '@/domain/shift-handover/stale';

export type ShiftProfile = 'operador' | 'tecnico_responsavel' | 'admin';

export interface CurrentShiftInfo {
  handoverId: string;
  operatorInId: string;
  operatorInName: string;
  since: string;
  /** Plantonista ainda ativo no cadastro — `false` trava a passagem (GUPM-273). */
  holderIsActive: boolean;
}

export type ShiftButtonState =
  | { kind: 'iniciar' }
  | { kind: 'sem-plantao-alerta' }
  | { kind: 'falha-consulta' }
  | { kind: 'iniciar-troca'; since: string; staleHours: number | null; holderIsActive: boolean }
  | {
      kind: 'plantao-alheio';
      holderName: string;
      since: string;
      canEnd: boolean;
      staleHours: number | null;
      holderIsActive: boolean;
    };

/**
 * Horas corridas de plantão quando ele já passou do limiar; `null` enquanto
 * estiver dentro dele.
 *
 * Devolve o NÚMERO, e não um booleano, porque a tela precisa dizer há quanto
 * tempo — "sem passagem há 19h" é acionável, "plantão vencido" não diz se
 * atrasou uma hora ou três dias.
 */
function staleHours(since: string, now: Date): number | null {
  const inicio = new Date(since);
  if (Number.isNaN(inicio.getTime())) return null;
  if (!isShiftStale(inicio, now)) return null;
  return Math.floor((now.getTime() - inicio.getTime()) / 3_600_000);
}

export function shiftButtonState(args: {
  profile: ShiftProfile;
  userId: string;
  current: CurrentShiftInfo | null;
  /** A consulta do plantão falhou — "não sei" é diferente de "não há". */
  queryFailed?: boolean;
  /**
   * Relógio injetado. O domínio não lê `Date.now()` por dentro: função que lê o
   * relógio sozinha não é testável sem congelar tempo, e aqui o relógio decide
   * se a tela acende um alarme.
   */
  now: Date;
}): ShiftButtonState {
  const { profile, userId, current, queryFailed, now } = args;

  if (queryFailed) return { kind: 'falha-consulta' };

  if (!current) {
    return profile === 'operador' ? { kind: 'iniciar' } : { kind: 'sem-plantao-alerta' };
  }

  if (current.operatorInId === userId) {
    // Uma porta só para o holder. Passar o plantão e encerrá-lo são escolhas
    // DENTRO do formulário (a lista de entrantes tem "— Ninguém —"), não estados
    // diferentes do botão: as duas saídas do turno passam pelo mesmo checklist.
    // A Rev.1 tinha um `canEndSelf` aqui para pendurar um segundo botão; ele
    // saiu com o botão.
    return {
      kind: 'iniciar-troca',
      since: current.since,
      staleHours: staleHours(current.since, now),
      holderIsActive: current.holderIsActive,
    };
  }

  return {
    kind: 'plantao-alheio',
    holderName: current.operatorInName,
    since: current.since,
    // GUPM-313: RT destrava também. Espelha o gate do banco em
    // `close_current_shift` — os dois têm que concordar, senão a tela oferece
    // um botão que a RPC recusa (ou esconde um direito que a pessoa tem).
    // Operador continua fora: a liberação do RT não afrouxa quem não tem
    // autoridade técnica.
    canEnd: profile === 'admin' || profile === 'tecnico_responsavel',
    staleHours: staleHours(current.since, now),
    holderIsActive: current.holderIsActive,
  };
}
